  /**
   * Hot Work Permit Component
   * ===============================
   * 
   * Individual form component 
   * 
   * Base Components:
   * - EXTENDS BaseFormComponent<HotWorkPermit> for form logic inheritance
   * - USES FormLayoutComponent for step navigation UI (in template)
   * 
   * Form Configuration:
   * - Form structure, steps, sections, and field organization are defined in the form model
   * - Signature and upload requirements are configured via form model metadata
   * - Component implements abstract methods: initializeForm() and getFormData()
   * - Component overrides setupDefaultStepValidation() for custom step validation rules
   * 
   * Key Functionality:
   * - Creates nested form groups for logical organization (permitDetails, requirements35Feet, drawingsReviewed, equipmentToolsMaterials, itemsCompleted, atmosphericMonitoring, approvals)
   * - Implements dynamic personnel management (up to 4 persons performing work)
   * - Provides comprehensive safety checklist for hot work activities
   * - Implements atmospheric monitoring with safety guidelines
   * - Implements form validation for hot work safety compliance
   * - Implements step validation with signature requirements for final step
   * - Flattens nested form structure for backend submission
   * 
   * Unique Complex Logic:
   * - Dynamic personnel management with add/remove functionality (up to 4 persons)
   * - Conditional utility location fields based on locate status with real-time validation updates
   * - Conditional validation setup for utility-related fields that become required only when locate is done
   * - Personnel visibility management with boolean array tracking for dynamic UI rendering
   * 
   * Form Model vs Component Responsibilities:
   * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
   * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
   * 
   * @author MacSafety Development Team
   * @version 1.0
   */

  import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../../../../models/forms/form-steps.interface';
import { HotWorkPermit } from '../../../../../models/forms/hot-work-permit/hot-work-permit.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';

/**
 * Component for handling Hot Work Permit submissions.
 * This form is used to document and authorize hot work activities,
 * ensuring proper safety measures and precautions are in place.
 */
@Component({
  selector: 'app-hot-work-permit',
  templateUrl: './hot-work-permit.component.html',
  styleUrls: ['./hot-work-permit.component.css']
})
export class HotWorkPermitComponent extends BaseFormComponent<HotWorkPermit> implements OnInit {
  // Form type identifier used for routing and form metadata lookup
  override formType = HotWorkPermit.getFormInfo().formType;
  
  // Instance of the form data model
  override model = new HotWorkPermit();
  
  // Define the form steps and their properties
  override steps: FormStep[] = HotWorkPermit.getFormSteps();

  personsPerformingWorkCount: number = 1;
  showAddPersonPerformingWorkButton: boolean = true;
  showPersonsPerformingWorkIndex: Array<boolean> = [true, false, false, false];

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(fb, formService, router, entraService, attachmentService, toastService, signatureService, screenSizeService);
    
    // Initialize form metadata first
    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
    

  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Initializes the form with its controls and validators.
   * Sets up the form group with all required fields and their validation rules.
   */
  protected override initializeForm(): void {
    this.form = this.fb.group({
      permitDetails: this.fb.group({
        date: [this.model.date, Validators.required],
        person1: [this.model.person1, Validators.required],
        person2: [this.model.person2],
        person3: [this.model.person3],
        person4: [this.model.person4],
        supervisorForeman: [this.model.supervisorForeman, Validators.required],
        recipientEmail: [this.model.recipientEmail, [Validators.email]],
        descriptionOfWork: [this.model.descriptionOfWork, Validators.required],
        workLocation1: [this.model.workLocation1, Validators.required],
        workLocation2: [this.model.workLocation2],
        locateDone: [this.model.locateDone],
        locateDate: [this.model.locateDate],
        utilitiesLocated: [this.model.utilitiesLocated],
        utilityProtection: [this.model.utilityProtection]
      }),
      requirements35Feet: this.fb.group({
        flammableMaterialsRemoved: [this.model.flammableMaterialsRemoved, Validators.required],
        floorWallOpeningsCovered: [this.model.floorWallOpeningsCovered, Validators.required],
        sparkPathEvaluated: [this.model.sparkPathEvaluated, Validators.required],
        ductsShutDown: [this.model.ductsShutDown, Validators.required]
      }),
      drawingsReviewed: this.fb.group({
        flowDiagrams: [this.model.flowDiagrams, Validators.required],
        undergroundSystems: [this.model.undergroundSystems, Validators.required],
        electricalPneumatic: [this.model.electricalPneumatic, Validators.required],
        drainsStorageSystems: [this.model.drainsStorageSystems, Validators.required]
      }),
      equipmentToolsMaterials: this.fb.group({
        faceShield: [this.model.faceShield, Validators.required],
        fireExtinguishingEquipment: [this.model.fireExtinguishingEquipment, Validators.required],
        cottonWoolClothing: [this.model.cottonWoolClothing, Validators.required],
        fireRetardantClothing: [this.model.fireRetardantClothing, Validators.required]
      }),
      itemsCompleted: this.fb.group({
        linesPurged: [this.model.linesPurged, Validators.required],
        linesClean: [this.model.linesClean, Validators.required],
        drainsSewersCovered: [this.model.drainsSewersCovered, Validators.required],
        valvesClosed: [this.model.valvesClosed, Validators.required],
        energySystemsLockedOut: [this.model.energySystemsLockedOut, Validators.required],
        blindsInstalled: [this.model.blindsInstalled, Validators.required],
        systemsDeenergized: [this.model.systemsDeenergized, Validators.required]
      }),
      atmosphericMonitoring: this.fb.group({
        oxygenLevel: [this.model.oxygenLevel],
        combustibleLevel: [this.model.combustibleLevel],
        carbonMonoxideLevel: [this.model.carbonMonoxideLevel],
        hydrogenSulfideLevel: [this.model.hydrogenSulfideLevel],
        samplingEquipmentType: [this.model.samplingEquipmentType],
        samplingConductedBy: [this.model.samplingConductedBy],
        calibrationCurrent: [this.model.calibrationCurrent],
        samplingDate: [this.model.samplingDate],
        samplingTime: [this.model.samplingTime]
      }),
      approvals: this.fb.group({
        initiatedBy: [this.model.initiatedBy, Validators.required],
        approvedBy: [this.model.approvedBy, Validators.required]
      })
    });
    this.setupConditionalValidators();
  }

  // Form group getters
  get permitDetailsFormGroup(): FormGroup {
    return this.form.get('permitDetails') as FormGroup;
  }

  get requirements35FeetFormGroup(): FormGroup {
    return this.form.get('requirements35Feet') as FormGroup;
  }

  get drawingsReviewedFormGroup(): FormGroup {
    return this.form.get('drawingsReviewed') as FormGroup;
  }

  get equipmentToolsMaterialsFormGroup(): FormGroup {
    return this.form.get('equipmentToolsMaterials') as FormGroup;
  }

  get itemsCompletedFormGroup(): FormGroup {
    return this.form.get('itemsCompleted') as FormGroup;
  }

  get atmosphericMonitoringFormGroup(): FormGroup {
    return this.form.get('atmosphericMonitoring') as FormGroup;
  }

  get approvalsFormGroup(): FormGroup {
    return this.form.get('approvals') as FormGroup;
  }

  /**
   * Retrieves the current form data and creates a new HotWorkPermit instance.
   * This method is called when submitting the form.
   */
  protected override getFormData(): HotWorkPermit {
    const formValue = this.form.value;
    
    // Flatten the nested form groups into a single object
    const flattenedData = {
      // Permit details (includes location & utilities)
      ...formValue.permitDetails,
      
      // Requirements within 35 feet
      ...formValue.requirements35Feet,
      
      // Drawings reviewed
      ...formValue.drawingsReviewed,
      
      // Equipment tools materials
      ...formValue.equipmentToolsMaterials,
      
      // Items completed
      ...formValue.itemsCompleted,
      
      // Atmospheric monitoring
      ...formValue.atmosphericMonitoring,
      
      // Approvals
      ...formValue.approvals
    };

    return flattenedData;

  }

  /**
   * Sets up validation rules for each step of the hot work permit form.
   * Each step has specific validation requirements that must be met before proceeding:
   * - Permit Details: Requires personnel info, description of work, location info
   * - Requirements Within 35 Feet: Requires all safety checklist items
   * - Drawings Reviewed: Requires confirmation of drawing review
   * - Equipment-Tools-Material: Requires equipment verification
   * - Items Completed: Requires completion of preparatory items
   * - Atmospheric Monitoring: Optional section
   * - Approvals: Requires initiation and approval signatures
   * 
   * The validation ensures that:
   * 1. All required fields are filled out
   * 2. Required signatures are collected
   * 3. Form data meets safety and compliance requirements
   */
   protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation('permit-details', this.permitDetailsFormGroup);
    this.setupStepValidation('requirements-35-feet', this.requirements35FeetFormGroup);
    this.setupStepValidation('drawings-reviewed', this.drawingsReviewedFormGroup);
    this.setupStepValidation('equipment-tools-materials', this.equipmentToolsMaterialsFormGroup);
    this.setupStepValidation('items-completed', this.itemsCompletedFormGroup);
    this.setupStepValidation('atmospheric-monitoring', this.atmosphericMonitoringFormGroup);
    this.setupStepValidation('approvals', this.approvalsFormGroup);
  }

  private setupConditionalValidators(): void {
    // Update the control requirements for the conditional location utilities fields based on the locateDone field value
    this.permitDetailsFormGroup.get('locateDone')?.valueChanges.subscribe(value => {
      const locateDateControl = this.permitDetailsFormGroup.get('locateDate');
      const utilitiesLocatedControl = this.permitDetailsFormGroup.get('utilitiesLocated');
      const utilityProtectionControl = this.permitDetailsFormGroup.get('utilityProtection');
      
      if (value === 'Yes') {
        locateDateControl?.setValidators([Validators.required]);
        utilitiesLocatedControl?.setValidators([Validators.required]);
        utilityProtectionControl?.setValidators([Validators.required]);
      } else {
        locateDateControl?.clearValidators();
        utilitiesLocatedControl?.clearValidators();
        utilityProtectionControl?.clearValidators();
      }
      
      locateDateControl?.updateValueAndValidity();
      utilitiesLocatedControl?.updateValueAndValidity();
      utilityProtectionControl?.updateValueAndValidity();
    });
  }

  addPersonPerformingWork() {
    if (this.personsPerformingWorkCount < 4) {
      this.personsPerformingWorkCount++;
      const personsPerformingWorkCount = this.personsPerformingWorkCount++
      this.showPersonsPerformingWorkIndex[personsPerformingWorkCount-1] = true;
      this.personsPerformingWorkCount = personsPerformingWorkCount;
      if (personsPerformingWorkCount === 4) {
        this.showAddPersonPerformingWorkButton = false;
      }
    }
  }
}